import React from "react";
import {
    Search, Activity, Server, Trash2, Loader2, Shield, Eye,
    ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Clock, User
} from "lucide-react";
import { ItemsPerPageDropdown } from "../../../../../components/ItemsPerPageDropdown ";
import type { FirewallLogEvent } from "./FirewallLogs";
import { formatBytes, getSeverityStyle, getActionStyle, formatTime, getProtocolName } from "./FirewallLogs";

export const COLUMNS_META = [
    { key: "user", label: "User" },
    { key: "time", label: "Time" },
    { key: "severity", label: "Severity" },
    { key: "action", label: "Action" },
    { key: "source", label: "Source" },
    { key: "destination", label: "Destination" },
    { key: "protocol", label: "Protocol" },
    { key: "app_service", label: "App/Service" },
    { key: "subtype", label: "Subtype" },
    { key: "policy_name", label: "Policy Name" },
    { key: "policy_id", label: "Policy ID" },
    { key: "sent_bytes", label: "Sent Bytes" },
    { key: "received_bytes", label: "Received Bytes" },
    { key: "duration", label: "Duration" },
    { key: "os_name", label: "OS Name" },
    { key: "mac_address", label: "MAC Address" },
    { key: "firewall_type", label: "Firewall Type" },
];

export const COLUMNS_MAP: Record<string, { label: string; width: string; cell: (log: FirewallLogEvent) => JSX.Element }> = {
    user: {
        label: "User",
        width: "w-[120px]",
        cell: (log: FirewallLogEvent) => (
            <td key="user" className="w-[120px] px-4 py-2 whitespace-nowrap overflow-hidden text-[10px] font-medium text-slate-700 dark:text-gray-300">
                {log.unauthuser ? (
                    <div className="flex items-center gap-1 truncate" title={log.unauthuser}>
                        <User size={10} className="text-slate-500 shrink-0" />
                        <span className="truncate font-bold">{log.unauthuser}</span>
                    </div>
                ) : (
                    <span className="text-slate-400 dark:text-gray-200">—</span>
                )}
            </td>
        )
    },
    time: {
        label: "Time",
        width: "w-[160px]",
        cell: (log: FirewallLogEvent) => (
            <td key="time" className="w-[160px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] font-mono">
                <div className="flex items-center gap-1.5 truncate">
                    <Clock size={10} className="text-slate-400 dark:text-gray-200 shrink-0" />
                    <span className="truncate">{formatTime(log.received_at)}</span>
                </div>
            </td>
        )
    },
    action: {
        label: "Action",
        width: "w-[110px]",
        cell: (log: FirewallLogEvent) => (
            <td key="action" className="w-[110px] px-4 py-2 whitespace-nowrap overflow-hidden">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black uppercase border ${getActionStyle(log.action)}`}>
                    {log.action}
                </span>
            </td>
        )
    },
    severity: {
        label: "Severity",
        width: "w-[110px]",
        cell: (log: FirewallLogEvent) => (
            <td key="severity" className="w-[110px] px-4 py-2 whitespace-nowrap overflow-hidden">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black uppercase border ${getSeverityStyle(log.severity)}`}>
                    {log.severity}
                </span>
            </td>
        )
    },
    source: {
        label: "Source (IP:Port)",
        width: "w-[210px]",
        cell: (log: FirewallLogEvent) => (
            <td key="source" className="w-[210px] px-4 py-2 whitespace-nowrap overflow-hidden font-mono text-[10px] text-cyan-600 dark:text-cyan-300/80">
                <div className="flex items-center gap-1 truncate" title={`${log.src_ip || ""}${log.src_port ? `:${log.src_port}` : ""}`}>
                    <span className="font-bold truncate">{log.src_ip || "—"}</span>
                    {log.src_port && <span className="text-slate-500 dark:text-gray-200 shrink-0">:{log.src_port}</span>}
                    {log.srcintf && (
                        <span className="text-[8px] bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-1 py-0.5 rounded text-slate-600 dark:text-slate-400 ml-1.5 uppercase font-sans font-bold shrink-0">
                            {log.srcintf}
                        </span>
                    )}
                </div>
            </td>
        )
    },
    destination: {
        label: "Destination (IP:Port)",
        width: "w-[210px]",
        cell: (log: FirewallLogEvent) => (
            <td key="destination" className="w-[210px] px-4 py-2 whitespace-nowrap overflow-hidden font-mono text-[10px] text-cyan-600 dark:text-cyan-300/80">
                <div className="flex items-center gap-1 truncate" title={`${log.dst_ip || ""}${log.dst_port ? `:${log.dst_port}` : ""}`}>
                    <span className="font-bold truncate">{log.dst_ip || "—"}</span>
                    {log.dst_port && <span className="text-slate-500 dark:text-gray-200 shrink-0">:{log.dst_port}</span>}
                    {log.dstintf && (
                        <span className="text-[8px] bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-1 py-0.5 rounded text-slate-600 dark:text-slate-400 ml-1.5 uppercase font-sans font-bold shrink-0">
                            {log.dstintf}
                        </span>
                    )}
                </div>
            </td>
        )
    },
    protocol: {
        label: "Protocol",
        width: "w-[90px]",
        cell: (log: FirewallLogEvent) => (
            <td key="protocol" className="w-[90px] px-4 py-2 whitespace-nowrap overflow-hidden">
                <span className="px-1.5 py-0.5 text-[9px] font-bold bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-gray-700/60 rounded text-slate-700 dark:text-gray-300 font-mono">
                    {getProtocolName(log.protocol)}
                </span>
            </td>
        )
    },
    app_service: {
        label: "App / Service",
        width: "w-[170px]",
        cell: (log: FirewallLogEvent) => (
            <td key="app_service" className="w-[170px] px-4 py-2 whitespace-nowrap overflow-hidden">
                <div className="flex flex-col truncate">
                    <span className="text-gray-900 dark:text-white text-[10px] font-bold truncate block" title={log.app || ""}>{log.app || "—"}</span>
                    {log.service && log.service !== log.app && (
                        <span className="text-slate-500 dark:text-gray-200 text-[8px] mt-0.5 truncate block" title={log.service}>{log.service}</span>
                    )}
                </div>
            </td>
        )
    },
    subtype: {
        label: "Subtype",
        width: "w-[110px]",
        cell: (log: FirewallLogEvent) => (
            <td key="subtype" className="w-[110px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] font-mono">
                <span className="truncate block" title={log.subtype || ""}>{log.subtype || "—"}</span>
            </td>
        )
    },
    policy_name: {
        label: "Policy Name",
        width: "w-[150px]",
        cell: (log: FirewallLogEvent) => (
            <td key="policy_name" className="w-[150px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px]">
                <span className="truncate block" title={log.policy_name || ""}>{log.policy_name || "—"}</span>
            </td>
        )
    },
    policy_id: {
        label: "Policy ID",
        width: "w-[100px]",
        cell: (log: FirewallLogEvent) => (
            <td key="policy_id" className="w-[100px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] font-mono">
                <span className="truncate block" title={String(log.policyid || "")}>{log.policyid || "—"}</span>
            </td>
        )
    },
    sent_bytes: {
        label: "Sent Bytes",
        width: "w-[110px]",
        cell: (log: FirewallLogEvent) => (
            <td key="sent_bytes" className="w-[110px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] font-mono">
                {log.sentbyte !== undefined && log.sentbyte !== null ? formatBytes(log.sentbyte) : "—"}
            </td>
        )
    },
    received_bytes: {
        label: "Received Bytes",
        width: "w-[110px]",
        cell: (log: FirewallLogEvent) => (
            <td key="received_bytes" className="w-[110px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] font-mono">
                {log.rcvdbyte !== undefined && log.rcvdbyte !== null ? formatBytes(log.rcvdbyte) : "—"}
            </td>
        )
    },
    duration: {
        label: "Duration",
        width: "w-[90px]",
        cell: (log: FirewallLogEvent) => (
            <td key="duration" className="w-[90px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] font-mono">
                {log.duration !== undefined && log.duration !== null ? `${log.duration}s` : "—"}
            </td>
        )
    },
    os_name: {
        label: "OS Name",
        width: "w-[120px]",
        cell: (log: FirewallLogEvent) => (
            <td key="os_name" className="w-[120px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px]">
                <span className="truncate block" title={log.osname || ""}>{log.osname || "—"}</span>
            </td>
        )
    },
    mac_address: {
        label: "MAC Address",
        width: "w-[150px]",
        cell: (log: FirewallLogEvent) => (
            <td key="mac_address" className="w-[150px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] font-mono">
                <span className="truncate block" title={log.srcmac || ""}>{log.srcmac || "—"}</span>
            </td>
        )
    },
    firewall_type: {
        label: "Firewall Type",
        width: "w-[120px]",
        cell: (log: FirewallLogEvent) => (
            <td key="firewall_type" className="w-[120px] px-4 py-2 whitespace-nowrap overflow-hidden text-slate-700 dark:text-gray-300 text-[10px] uppercase">
                <span className="truncate block" title={log.firewall_type || ""}>{log.firewall_type || "—"}</span>
            </td>
        )
    }
};

interface LogsTabProps {
    setLiveLogs: React.Dispatch<React.SetStateAction<any[]>>;
    actionFilter: string;
    setActionFilter: (act: string) => void;
    severityFilter: string;
    setSeverityFilter: (sev: string) => void;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    debouncedSearch: string;
    isLive: boolean;
    setIsLive: (live: boolean) => void;
    showColumnSelector: boolean;
    setShowColumnSelector: (show: boolean) => void;
    visibleColumns: string[];
    setVisibleColumns: React.Dispatch<React.SetStateAction<string[]>>;
    isLogsLoading: boolean;
    activeLogs: FirewallLogEvent[];
    setSelectedLogId: (logOrId: any) => void;
    limit: number;
    setLimit: (limit: number) => void;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    currentPage: number;
    startRecord: number;
    endRecord: number;
    totalRecords: number;
    totalPages: number;
}

export default function LogsTab({
    setLiveLogs,
    actionFilter,
    setActionFilter,
    severityFilter,
    setSeverityFilter,
    searchTerm,
    setSearchTerm,
    debouncedSearch,
    isLive,
    setIsLive,
    showColumnSelector,
    setShowColumnSelector,
    visibleColumns,
    setVisibleColumns,
    isLogsLoading,
    activeLogs,
    setSelectedLogId,
    limit,
    setLimit,
    setCurrentPage,
    currentPage,
    startRecord,
    endRecord,
    totalRecords,
    totalPages
}: LogsTabProps) {
    return (
        <div className="animate-fade-in flex flex-col">
            {/* Live events table container */}
            <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                {/* Search & filter bars header */}
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4 bg-slate-50/50 dark:bg-slate-950/20 shrink-0">
                    {/* Left: When Live show streaming indicator, when Paused show Action & Severity filters */}
                    {isLive ? (
                        <div className="flex items-center gap-2 py-0.5">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                                Live Stream Active
                            </span>
                            <span className="text-[9px] text-slate-500 font-semibold hidden sm:inline">
                                — Pause feed to filter by Action, Severity, or Search
                            </span>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-4 animate-fade-in">
                            {/* Action filter */}
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Action:</span>
                                <div className="flex items-center bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-0.5">
                                    {["All", "Accept", "Deny"].map((act) => {
                                        const isActive = actionFilter === act;
                                        return (
                                            <button
                                                key={act}
                                                onClick={() => {
                                                    setActionFilter(act);
                                                    setCurrentPage(1);
                                                }}
                                                className={`px-2.5 py-1 rounded text-[9px] font-bold uppercase transition-all select-none focus:outline-none
                                                    ${isActive
                                                        ? act === "Deny"
                                                            ? "bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/20 shadow-[0_0_6px_rgba(239,68,68,0.1)] font-black"
                                                            : act === "Accept"
                                                                ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shadow-[0_0_6px_rgba(16,185,129,0.1)] font-black"
                                                                : "bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-black shadow-sm"
                                                        : "text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                                                    }`}
                                            >
                                                {act}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Severity filter */}
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Severity:</span>
                                <div className="flex items-center bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-0.5">
                                    {["All", "Information", "Notice", "Warning"].map((sev) => {
                                        const isActive = severityFilter === sev;
                                        return (
                                            <button
                                                key={sev}
                                                onClick={() => {
                                                    setSeverityFilter(sev);
                                                    setCurrentPage(1);
                                                }}
                                                className={`px-2.5 py-1 rounded text-[9px] font-bold uppercase transition-all select-none focus:outline-none
                                                    ${isActive
                                                        ? "bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-black shadow-sm"
                                                        : "text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                                                    }`}
                                            >
                                                {sev}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2.5 ml-auto w-full md:w-auto justify-end">
                        {/* Dynamic Search Input (Only displayed when paused) */}
                        {!isLive && (
                            <div className="relative group animate-fade-in">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 group-focus-within:text-orange-500 transition-colors duration-250" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    placeholder="Search paused logs..."
                                    className="w-56 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:border-orange-500/50 rounded-lg pl-8 pr-3 py-1.5 text-[10px] text-gray-900 dark:text-white placeholder:text-slate-400 transition-all font-semibold focus:outline-none"
                                />
                            </div>
                        )}

                        {/* Start/Pause trigger */}
                        <button
                            onClick={() => {
                                setIsLive(!isLive);
                                setCurrentPage(1);
                            }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all select-none focus:outline-none shrink-0
                                ${isLive
                                    ? "bg-red-500/10 hover:bg-red-500/20 border-red-500/30 hover:border-red-500/50 text-red-600 dark:text-red-400"
                                    : "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/30 hover:border-emerald-500/50 text-emerald-600 dark:text-emerald-400"
                                }`}
                        >
                            <Activity size={12} className={isLive ? "animate-pulse text-red-500 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"} />
                            {isLive ? "Pause Feed" : "Go Live"}
                        </button>

                        {/* Customizable display columns popover */}
                        <div className="relative shrink-0">
                            <button
                                onClick={() => setShowColumnSelector(!showColumnSelector)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-950 hover:bg-slate-100 dark:hover:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white text-[10px] font-bold uppercase tracking-wider transition-all focus:outline-none"
                            >
                                <Server size={13} className="text-orange-500" />
                                Columns
                            </button>
                            {showColumnSelector && (
                                <>
                                    <div className="fixed inset-0 z-20" onClick={() => setShowColumnSelector(false)} />
                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-3.5 z-30 max-h-64 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-2 duration-150">
                                        <div className="text-[9px] font-black uppercase text-slate-500 dark:text-slate-400 tracking-widest border-b border-slate-200 dark:border-slate-800 pb-1.5 mb-2 px-1">
                                            Display Columns
                                        </div>
                                        <div className="space-y-1.5">
                                            {COLUMNS_META.map(col => {
                                                const isVisible = visibleColumns.includes(col.key);
                                                return (
                                                    <label
                                                        key={col.key}
                                                        className="flex items-center gap-2.5 px-1.5 py-1 hover:bg-slate-100 dark:hover:bg-slate-900 rounded cursor-pointer text-[10px] text-slate-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white font-medium select-none transition-colors"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isVisible}
                                                            onChange={() => {
                                                                if (isVisible) {
                                                                    if (visibleColumns.length > 1) {
                                                                        setVisibleColumns(prev => prev.filter(k => k !== col.key));
                                                                    }
                                                                } else {
                                                                    setVisibleColumns(prev => [...prev, col.key]);
                                                                }
                                                            }}
                                                            className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-800 text-orange-500 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0"
                                                        />
                                                        {col.label}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Stream clear button */}
                        {isLive && (
                            <button
                                onClick={() => {
                                    setLiveLogs([]);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-950 hover:bg-red-50 dark:hover:bg-red-950/20 border border-slate-200 dark:border-slate-800 hover:border-red-300 dark:hover:border-red-900/30 rounded-lg text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 text-[10px] font-bold uppercase tracking-wider transition-all focus:outline-none"
                            >
                                <Trash2 size={12} />
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Logs display table */}
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full table-fixed text-left text-xs border-collapse min-w-[1200px]">
                        <thead className="bg-slate-50 dark:bg-slate-950 sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
                            <tr>
                                {visibleColumns.map(colKey => {
                                    const col = COLUMNS_MAP[colKey as keyof typeof COLUMNS_MAP];
                                    if (!col) return null;
                                    return (
                                        <th
                                            key={colKey}
                                            className={`px-4 py-2.5 text-[9px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest ${col.width || "w-auto"}`}
                                        >
                                            {col.label}
                                        </th>
                                    );
                                })}
                                <th className="px-4 py-2.5 w-[60px] text-center text-[9px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest">Detail</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40">
                            {isLogsLoading ? (
                                <tr>
                                    <td colSpan={visibleColumns.length + 1} className="py-24 text-center">
                                        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading firewall logs...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : activeLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColumns.length + 1} className="py-24 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-60 dark:opacity-40">
                                            <Shield size={24} className="text-slate-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                                {isLive ? "Listening for live firewall events..." : "No logs found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                activeLogs.map((log) => (
                                    <tr
                                        key={log.id}
                                        onClick={() => setSelectedLogId(log)}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors cursor-pointer border-b border-slate-100 dark:border-slate-900/35 animate-fade-in"
                                    >
                                        {visibleColumns.map(colKey => {
                                            const col = COLUMNS_MAP[colKey as keyof typeof COLUMNS_MAP];
                                            if (!col) return null;
                                            return col.cell(log);
                                        })}
                                        <td className="w-[60px] px-4 py-2 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => setSelectedLogId(log)}
                                                className="p-1 hover:bg-orange-500/10 dark:hover:bg-orange-600/20 border border-transparent hover:border-orange-500/20 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-white rounded transition-all focus:outline-none"
                                            >
                                                <Eye size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination footer (Only shown when live feed is paused) */}
                {!isLive && (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50/50 dark:bg-slate-950/20 border-t border-slate-200 dark:border-slate-800 shrink-0">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={limit}
                                onChange={(v) => {
                                    setLimit(v);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400 tracking-widest uppercase">
                                <span className="text-gray-900 dark:text-gray-200 font-bold">{startRecord}</span>
                                {" - "}
                                <span className="text-gray-900 dark:text-gray-200 font-bold">{endRecord}</span>
                                <span className="mx-1.5 text-slate-400 dark:text-slate-700">/</span>
                                <span className="text-gray-900 dark:text-gray-200 font-bold">{totalRecords}</span>
                            </span>
                            {debouncedSearch && (
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-black uppercase tracking-widest transition-colors focus:outline-none"
                                >
                                    Clear search
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors focus:outline-none"
                            >
                                <ChevronsLeft size={13} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors focus:outline-none"
                            >
                                <ChevronLeft size={13} />
                            </button>
                            <div className="px-3 py-1 bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400 rounded-lg text-[10px] font-black font-mono">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage((p) => p + 1)}
                                className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors focus:outline-none"
                            >
                                <ChevronRight size={13} />
                            </button>
                            <button
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(totalPages)}
                                className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors focus:outline-none"
                            >
                                <ChevronsRight size={13} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
